import { customEvent, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useEffect, useState } from "react";
import { MubinsoConversionScreen } from "./MubinsoConversionScreen";
import { MubinsoLeadModal } from "./MubinsoLeadModal";
import { MubinsoParlorShareMapCard } from "./MubinsoMapPins";
import { MubinsoIconTimeline } from "./MubinsoTimeline";
import {
  MubinsoCallCareCard,
  MubinsoReportPreviewCard,
  MubinsoCostFormulaCard,
  MubinsoCtaButton,
  MubinsoGoiTalk,
  MubinsoIconRowCard,
  MubinsoKeepAll,
  MubinsoOrange as Orange,
  MubinsoReviewCarousel,
  MubinsoScreenLayout,
  MubinsoStepIntro as StepIntro,
} from "./MubinsoUI";
import { buildMubinsoLeadMessage } from "./mubinsoLeadMessage";
import { MubinsoEntry, mubinsoEntryTrigger } from "./mubinsoConstants";
import { MUBINSO_E4_LEAD_MODAL_PROPS } from "./mubinsoE4Constants";

const TOTAL_STEPS = 5;

/**
 * 무빈소 절차 퍼널 E4(신뢰 구간 개편). 실험에서 현행 MubinsoProcedureFunnelB 의 처치군이다.
 *
 * 현행 4단계(무빈소란 → 진행 → 장례식장 확인 → 비용) 뒤에, 고객이 "이걸 다 직접 해야 하나" 하고
 * 막막해지는 지점에 B-5 를 더해 고이가 대신한다고 답한 뒤 전환 화면으로 보낸다.
 * Figma 의 「이 많은 결정」 화면은 비용 화면과 같은 이야기라 빼고, 그 내용(정할 게 많다)은
 * B-4 문구에 담았다(마케팅 피드백 「퍼널이 길다」, 주완 2026-10-30).
 *
 * 문구·순서는 Figma 「B퍼널 = 절차 소구」(21830:19742 안, 22536:19613 부터)가 기준이다.
 * 명세: wiki/plans/mubinso-funnel-trust.md
 */
export default function MubinsoProcedureFunnelE4({
  topicAnswer,
  onComplete,
  onBack,
  entry,
}: {
  /** B-1 의 「이전」. 퍼널 밖(첫 화면. E4 는 첫 화면이 Q1 을 겸한다)으로 돌아간다 */
  onBack: () => void;
  /** Q1 답. E4 에는 Q2(상황)가 없어 상황은 결과 모달에서 받는다 */
  topicAnswer: string;
  onComplete: () => void;
  /** 진입 구분. 퍼널별 개별 이벤트({p}-cta_main 등) 발화에 쓴다 */
  entry: MubinsoEntry;
}) {
  const [step, setStep] = useState(1);
  const [openModal, setOpenModal] = useState<null | "check" | "guide">(null);

  // 단계 전환 시 스크롤 최상단 + 퍼널별 진행 이벤트.
  // 현행(procedure)과 단계 구성이 달라 funnel 값을 나눈다
  useEffect(() => {
    window.scrollTo(0, 0);
    customEvent({
      newGtm: { action: "mubinso_funnel_step", properties: { funnel: "procedure_e4", step } },
    });
  }, [step]);

  const goNext = () => {
    customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: `funnel-next-${step}` } });
    setStep((s) => s + 1);
  };

  // 정보 화면이 5장이라 앞 화면을 다시 보고 싶을 수 있다. 화면 안에 돌아갈 길이 없으면 브라우저
  // 뒤로 가기를 누르게 되고, 그건 단계 이동이 아니라 이탈 방지 팝업(useRouterChangeLoad)으로 이어진다
  const goPrev = () => {
    customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: `funnel-prev-${step}` } });
    if (step === 1) onBack();
    else setStep((s) => s - 1);
  };

  const stepLayout = (current: number) => ({
    progress: { current, total: TOTAL_STEPS },
    onPrev: goPrev,
    footer: <MubinsoCtaButton label="다음" onClick={goNext} />,
  });

  // 1 - 무빈소란
  if (step === 1) {
    return (
      <MubinsoScreenLayout {...stepLayout(1)}>
        <StepIntro
          title={
            <>
              무빈소 장례는
              <br />
              <Orange>빈소가 없는 장례</Orange>예요.
            </>
          }
          desc={
            <>
              빈소가 없어서 손님을 받지 않고,
              <br />
              가족들만 모여 고인을 보내드려요.
            </>
          }
        />

        {/* 특징 3가지: 누가(가족) · 어디서(집) · 얼마(비용). 윗줄 "~없이" + 아랫줄 명사형 핵심어로 맞췄다.
            "빈소 대신 자택에서" 는 자택에서 장례를 치르는 것처럼 읽혀 "빈소 밤샘 없이 · 집에서 대기" 로 바꿨다("휴식" 은 아무것도 안 하는 느낌이라 "대기").
            윗줄은 360px 폭에서 한 줄에 들어가도록 7자 이내 */}
        <Spacing gap={24} />
        <MubinsoIconRowCard
          items={[
            { icon: "icon_Graphic_Family", top: "조문객 없이", bottom: "가족 중심" },
            { icon: "icon_Graphic_Home", top: "빈소 밤샘 없이", bottom: "집에서 대기" },
            { icon: "icon_Graphic_PriceDrop", top: "빈소·접객 없이", bottom: "비용 경감" },
          ]}
        />

        {/* 고객이 가장 걱정하는 "제대로 안 해 주는 거 아냐?" 에 고이가 직접 답한다 */}
        <Spacing gap={16} />
        <MubinsoGoiTalk>
          무빈소 장례도 <b>안치, 입관, 발인과 같은 절차는 완전히 동일</b>
          하게 진행돼요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // 2 - 실제 진행
  if (step === 2) {
    return (
      <MubinsoScreenLayout {...stepLayout(2)}>
        <StepIntro
          title={
            <>
              무빈소 장례는
              <br />
              <Orange>어떤 식</Orange>으로 진행되나요?
            </>
          }
          desc="무빈소 장례는 다음과 같은 순서로 진행돼요."
        />

        <Spacing gap={32} />
        {/* 며칠에 걸쳐 가족이 무엇을 하는지가 보이게: 집 → 입관식(가족 참여, 강조) → 발인 */}
        <MubinsoIconTimeline
          items={[
            {
              icon: "icon_Graphic_Home",
              phase: "장례 첫날",
              title: "가족들은 자택에 머무릅니다.",
              // 안치를 넣었다(초안): "그동안 고인은 어디 계시나" 가 비어 있었다. 설명은 해요체로 통일
              desc: "고인은 장례식장 안치실에 편히 모시고,\n가족들은 빈소 대신 자택에서 시간을 보내요.",
            },
            {
              icon: "icon_Graphic_Suit",
              phase: "다음 날 · 발인 전",
              title: "입관식에 참여합니다.",
              desc: "고인을 마지막으로 뵐 수 있는 자리이자,\n가족들이 마음을 담아 보내는 중요한 시간이에요.",
              highlight: true,
            },
            {
              icon: "icon_Graphic_Bus",
              // 날짜는 바로 위 "다음 날 · 발인 전" 이 정하고, 이 단계는 입관식 바로 다음으로 읽힌다
              phase: "입관 후 이어서",
              // Figma 는 "발인" 한 단어뿐이라 초안 문구. 상담에서 "화장 일정이 밀리지 않나" 를 많이 묻는다
              // (Notion 2-4-3 STT 분석). 형식 님 라이팅 확인 필요
              title: "발인 후 화장장으로 모십니다.",
              // 다른 단계처럼 "무슨 일이 일어나는지" 를 설명한다(고이가 하는 일은 B-5 에서). 상담 순서 화장 → 장지.
              // 「장지」는 업계 말이라 고객 말 「납골당이나 산소」로(마케팅 피드백)
              desc: "화장을 마친 뒤 납골당이나 산소로 모시면,\n모든 장례 절차가 마무리돼요.",
            },
          ]}
        />

        <Spacing gap={24} />
        <MubinsoGoiTalk>
          입관식을 진행하는 <b>장례지도사가 중요한 이유</b>예요. 일반적으로 장례식장 소속
          장례지도사는 <b>여러 가족분들을 동시에 담당</b>해요. 그래서 고이 장례지도사와 같은
          세심함을 기대하기 어려워요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // 3 - 장례식장 확인
  if (step === 3) {
    return (
      <MubinsoScreenLayout {...stepLayout(3)}>
        {/* 설명: Figma 는 세 줄이지만 둘째 줄이 짧게 끊겨 두 줄로 합쳤다(주완 2026-10-30).
            둘째 줄은 360px 화면 글 폭(312px)에 309px 로 한 줄에 들어간다 */}
        <StepIntro
          title={
            <>
              무빈소 장례에서는
              <br />
              <Orange>장례식장 확인</Orange>이 가장 중요해요.
            </>
          }
          desc={
            <>
              장례식장은 빈소와 음식에서 수익을 얻어요.
              <br />
              무빈소 장례는 수익이 적어, 불가능한 곳이 많아요.
            </>
          }
        />

        <Spacing gap={24} />
        <MubinsoParlorShareMapCard />

        <Spacing gap={16} />
        <MubinsoGoiTalk>
          상황이 발생한 뒤에 급하게 찾으려면 <b>무빈소로 진행하지 못할 수도 있어요.</b> 그래서
          가능한 장례식장을 최대한 빠르게 확인하는 것이 좋아요.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // 4 - 비용 항목
  if (step === 4) {
    return (
      <MubinsoScreenLayout {...stepLayout(4)}>
        {/* 제목은 「정할 게 많다」로 열고 설명 · 카드 결과 줄이 비용을 맡는다(주완 2026-10-30, 형식 님 확인 필요).
            「장례식장 말고도」 는 B-3 「장례식장 확인이 가장 중요해요」 를 받고,
            「정해야 할 것」 은 B-5 「이 모든 절차」 가 가리킬 대상이 된다 */}
        <StepIntro
          title={
            <>
              장례식장 말고도,
              <br />
              직접 <Orange>정해야 할 것</Orange>이 많아요.
            </>
          }
          // 첫 줄이 더 길게(196 / 186px)
          desc={
            <>
              고르는 것마다 비용이 달라져서,
              <br />
              미리 확인해 두는 것이 좋아요.
            </>
          }
        />

        <Spacing gap={24} />
        {/* 계산식: 항목을 더하면 내 장례 비용. 순서는 장례 진행 순서(안치·입관 → 이동 → 화장).
            Figma 3줄(시설 · 용품·지도사 · 차량)을 나눠 5줄로(주완 2026-10-30). 오른쪽 기준은 초안,
            장례지도사는 1명이라 「인원이 많을수록」이 어색해 「장례 인력」으로, 「등급이 높을수록」은 애매해
            「고급일수록」으로(피드백 2026-10-30, 운영 기준 확인 필요).
            화장장은 거리가 아니라 고인 주민등록 주소지로 관내/관외가 갈려서 "관외이면"(~일수록 아님) */}
        <MubinsoCostFormulaCard
          title="상조가 없다면, 직접 정해야 하는 것들"
          rows={[
            { icon: "icon_Graphic_Building", label: "장례식장", rule: "안치 기간이 길수록" },
            { icon: "icon_Graphic_Urn", label: "장례 용품", rule: "고급일수록" },
            { icon: "icon_Graphic_Suit", label: "장례 인력", rule: "인원이 많을수록" },
            { icon: "icon_Graphic_Bus", label: "차량 · 이송", rule: "멀수록" },
            { icon: "icon_Graphic_BuildingRoof", label: "화장장", rule: "관외이면" },
          ]}
          resultLabel="내 장례 비용"
          resultValue="? 원"
        />

        <Spacing gap={16} />
        <MubinsoGoiTalk>
          장례를 하는 동안 <b>슬퍼할 시간도 부족한데</b>, 추가금이나 노잣돈 같은{" "}
          <b>관행적인 비용을 요구하는 경우</b>도 있어요. 그래서 장례식장과 화장장, 차량 이송{" "}
          <b>비용을 미리 확인하고 견적을 받아보는 것이 좋아요.</b>
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // 5 - 고이가 돕고 대신
  if (step === 5) {
    return (
      <MubinsoScreenLayout {...stepLayout(5)}>
        {/* 제목은 Figma 수정본(형식 님 라이팅, 2026-10-30): 「대신」「함께」 주황.
            설명은 360px 에서 「발인까 / 지」 로 깨져 줄였다. 첫 줄이 더 길게(233 / 196px) */}
        <StepIntro
          title={
            <>
              고이는 이 모든 절차를 <Orange>대신</Orange>하고,
              <br />
              모든 과정을 <Orange>함께</Orange>합니다.
            </>
          }
          desc={
            <>
              전담 장례지도사가 이송부터 발인까지
              <br />
              곁에서 하나하나 챙겨드려요.
            </>
          }
        />

        <Spacing gap={24} />
        {/* 사망진단서는 의사가 발급하고 사망 신고는 가족이 해서 고이가 대신할 수 없다. 그래서 "대신" 이 아니라 "모두 챙김" 으로 적고
            (「결정 · 서류 안내」 는 알려만 주는 느낌이라 바꿈, 주완 2026-10-31),
            고이가 직접 하는 예약 · 이송을 앞에 둔다(주완 2026-10-30: 4가지 2×2) */}
        <MubinsoCallCareCard
          callLabel="24시간 · 365일 전화 대기"
          callTitle="고이에 전화 한 통이면"
          items={[
            { icon: "icon_Graphic_Suit", label: "전담\n장례지도사" },
            { icon: "icon_Graphic_Building", label: "장례식장\n예약 · 이송" },
            // B-4 「고르는 것마다 비용이 달라져요」 에 답한다. 화장장 · 납골당 예약보다 와닿아서 이 칸을 바꿨다(주완 2026-10-30).
            // 아이콘은 저울(공정 · 투명). 가격표(PriceList)는 옆 칸 「서류 · 행정」 과 같은 파란 문서라 구분이 안 됐다
            { icon: "icon_Graphic_Scale", label: "투명한\n가격 공개" },
            { icon: "icon_Graphic_Write", label: "서류 · 행정\n모두 챙김" },
          ]}
        />

        <Spacing gap={16} />
        <MubinsoGoiTalk>
          오직 추모에만 집중할 수 있도록, <b>복잡한 절차와 과정은 고이가 대신</b>해드려요. 고이는{" "}
          <b>노잣돈이나 추가금과 같은 관행적인 모든 비용을 거부</b>
          합니다.
        </MubinsoGoiTalk>
      </MubinsoScreenLayout>
    );
  }

  // 6 - 전환
  return (
    <>
      <MubinsoConversionScreen
        entry={entry}
        onPrev={goPrev}
        headline={
          <>
            {/* 「우리 동네」 는 B-3 영등포구 예시를 받아 「그럼 우리 동네는?」 으로 잇는다(주완 2026-10-30).
                끝은 형식 님 원문 「확인해 드릴게요」. 「무료」 는 아래 CTA 에 있어 제목에서는 뺐다.
                「예상 비용, 빠르게 확인해 드릴게요」 는 360px 에서 넘쳐(319px) 「까지」 로 이었다 */}
            우리 동네 <Orange>무빈소 장례식장</Orange>과
            <br />
            예상 비용까지 확인해 드릴게요
          </>
        }
        sub={
          "무빈소 장례 2,000건 이상의 경험을 바탕으로\n내 상황에 맞는 정보만 정리해서 알려드릴게요."
        }
        checkItems={[]}
        // 받아보실 자료를 견적서처럼 미리 보여 준다. 값은 비워 두고 신청하면 채워진다는 뜻(B-4 「? 원」과 같은 표현)
        checkCard={
          <BaseStack width="100%" spacing={16}>
            <MubinsoReportPreviewCard
              title="고이가 정리해서 알려드려요"
              rows={[
                // 절차 퍼널이라 절차를 맨 위에. 「필요한 추가 항목 ? 개」 는 예상 장례 비용과 겹쳐 이 줄로 바꿨다(주완 2026-10-31).
                // 단계 수는 채울 정보가 아니라 오른쪽 값은 비운다
                {
                  icon: "icon_Graphic_Guidebook",
                  label: "무빈소 장례 절차",
                  sub: "단계별로 자세히",
                },
                {
                  icon: "icon_Graphic_Building",
                  label: "무빈소 되는 장례식장",
                  sub: "내 지역 기준",
                  value: "? 곳",
                },
                {
                  icon: "icon_Graphic_PriceList",
                  label: "예상 장례 비용",
                  sub: "내 상황 기준",
                  value: "? 원",
                },
              ]}
            />
            {/* 고이 박스 대신 후기(주완 2026-10-31, C · D 결과와 같은 틀). 절차를 대신 · 함께한 실제 후기만
                고이 후기 API(sangjo_reviews) 원문 문장을 그대로 잘라 썼다(맞춤법도 고치지 않음). 문장 앞 접속어는 잘랐다.
                이름은 운영 API 가 가려서 주는 값 그대로(지어내지 않는다). 운영 후기 id: 2346 · 2943 · 2378 · 2440. 무빈소 퍼널이라 무빈소 상품 후기만(주완 2026-10-31).
                사이트에서 원 후기가 지워지면 여기서도 뺀다. 한 사람 후기는 한 장만
                무빈소 상품 후기만이 아니라 제목에 「무빈소」 를 넣지 않는다. 첫 장은 무빈소 후기로 B-2(집에서 대기)와 이어진다 */}
            <MubinsoReviewCarousel
              title="고이와 함께 장례를 치른 가족분들의 후기"
              onMove={(card, firstView) => {
                // 넘길 때마다 한 번(클릭 수) + 그 후기를 처음 볼 때 번호와 함께 한 번(몇 개까지 봤나)
                customEvent({
                  newGtm: { path: "mubinso_funnel", trigger_and_target: "prc-review" },
                });
                if (firstView) {
                  customEvent({
                    newGtm: { path: "mubinso_funnel", trigger_and_target: `prc-review-${card}` },
                  });
                }
              }}
              reviews={[
                {
                  text: (
                    <>
                      지도사님께서 잘 정리된 스케쥴표 및 앞으로 어떤식으로{" "}
                      <b>무빈소 장례가 이루어지는지 아주 이해하기쉽게</b> 설명해주셔서 안심하고
                      귀가하였습니다
                    </>
                  ),
                  meta: "김OO 님 · 무빈소 · 2026.07",
                },
                {
                  text: (
                    <>
                      무엇을 어떻게 준비해야 할지 막막했지만, 장례지도사님께서{" "}
                      <b>차분하고 꼼꼼하게 하나하나 안내</b>해 주신 덕분에 큰 어려움 없이 장례를
                      진행할 수 있었습니다
                    </>
                  ),
                  meta: "진OO 님 · 무빈소 · 2026.10",
                },
                {
                  text: (
                    <>
                      장례 절차 하나하나를 차분하고 정확하게 설명해 주셨고,{" "}
                      <b>필요한 부분은 미리 세심하게 챙겨</b> 주셔서 가족들이 당황하지 않고
                      외할머니를 편안하게 모실 수 있었습니다.
                    </>
                  ),
                  meta: "박OO 님 · 무빈소 · 2026.07",
                },
                {
                  text: (
                    <>
                      복잡한 절차나 <b>서류 처리</b>, 예법 등에 대해 유가족이 당황하지 않도록
                      눈높이에 맞춰 차분하고 친절하게 설명해 주셔서{" "}
                      <b>오롯이 고인을 모시는 데에만 집중</b>할 수 있었습니다.
                    </>
                  ),
                  meta: "석OO 님 · 무빈소 · 2026.07",
                },
              ]}
            />
          </BaseStack>
        }
        primaryLabel="장례식장과 예상 비용 무료로 확인하기"
        onPrimary={() => {
          // 현행과 같은 이름으로 발화해야 A/B 를 같은 축에서 비교할 수 있다
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-info_cta" } });
          customEvent({
            newGtm: {
              path: "mubinso_funnel",
              trigger_and_target: mubinsoEntryTrigger(entry, "cta_main"),
            },
          });
          setOpenModal("check");
        }}
        secondaryLabel="무빈소 가이드 받아보기"
        secondaryVariant="link"
        secondaryHint="아직 천천히 알아보는 중이라면"
        onSecondary={() => {
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "prc-guide_lnk" } });
          setOpenModal("guide");
        }}
      />

      <MubinsoLeadModal
        isOpen={openModal === "check"}
        onClose={() => setOpenModal(null)}
        // "비용을 받아보기" 가 어색해 결과 CTA(무료로 확인하기) · Figma 버튼과 같은 "확인하기" 로 맞췄다.
        // 앞부분 주황은 Figma(형식 님) 그대로. 숫자는 다른 화면과 같은 "건"
        // 360px 에서 「확인하 / 기」 로 끊기지 않게 낱말 단위로 줄바꿈
        title={
          <MubinsoKeepAll>
            <Orange>장례식장과 예상 비용</Orange> 확인하기
          </MubinsoKeepAll>
        }
        description={
          <MubinsoKeepAll>
            고이가 2,000건 이상의 무빈소 장례 경험을 바탕으로, 필요하신 정보와 자료를 빠르게
            전해드릴게요.
          </MubinsoKeepAll>
        }
        buttonText="동의하고 무료로 확인하기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "check",
            situation: "",
            topic: topicAnswer,
          })
        }
        {...MUBINSO_E4_LEAD_MODAL_PROPS}
        entry={entry}
        onSubmitted={onComplete}
      />
      <MubinsoLeadModal
        isOpen={openModal === "guide"}
        onClose={() => setOpenModal(null)}
        title="무빈소 가이드 받아보기"
        freeTag
        description={
          <MubinsoKeepAll>
            무빈소 장례 2,000건 이상을 진행하며 가족분들이 가장 궁금해하셨던 내용을 담았어요. 절차,
            예상 비용, 장례 준비까지 알려드려요.
          </MubinsoKeepAll>
        }
        buttonText="동의하고 자료 받아보기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "info",
            situation: "",
            topic: topicAnswer,
          })
        }
        {...MUBINSO_E4_LEAD_MODAL_PROPS}
        entry={entry}
        // 결과 화면 신청과 나눠 보려고 가이드 창 제출만 따로 센다(A 와 같은 이름)
        onLeadSuccess={() =>
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "prc-guide_ok" } })
        }
        onSubmitted={onComplete}
      />
    </>
  );
}
